import { useSyncExternalStore } from 'react';

/**
 * Whether this is a phone-width screen, as a value rather than a CSS class.
 *
 * ── WHY NOT JUST `sm:hidden` ──
 * Because a list and its card equivalent contain the same controls, and CSS
 * hiding renders BOTH into the DOM. That is fine for decoration and wrong for
 * anything interactive:
 *
 *   - Two elements per row carry the same `aria-label`, so a screen reader
 *     announces every field twice and a keyboard user tabs through a copy of
 *     the form they cannot see.
 *   - Every `getByLabel` and `getByRole` in the test suite matches twice, and
 *     `.nth(1)` silently addresses the hidden copy of the first row rather than
 *     the second row.
 *
 * So the choice is made once, here, and only one layout is built.
 *
 * `useSyncExternalStore` rather than `useState` + an effect: it reads the match
 * during render, so the first paint is already correct instead of flashing the
 * desktop layout and then swapping.
 *
 * 640px is Tailwind's `sm`, kept identical on purpose - a component that
 * switches at one width while its CSS switches at another is a bug nobody
 * finds until a tablet.
 */
const QUERY = '(max-width: 639px)';

function subscribe(onChange: () => void) {
  const mql = window.matchMedia(QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

export function useIsPhone(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(QUERY).matches,
    // No server rendering here, but the third argument is required and a
    // desktop default is the safer guess for anything that ever gets one.
    () => false,
  );
}
